import React, { useMemo, useState } from 'react'
import { subDays } from 'date-fns'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import Banner, { BANNER_COLOR_DANGER } from '@divvy-web/skylab.banner'
import ExpansionPanel from '@divvy-web/skylab.expansionpanel'
import FileViewer from '@divvy-web/skylab.fileviewer'
import Icon from '@divvy-web/skylab.icon'
import Spinner, { SpinnerSize } from '@divvy-web/skylab.spinner'
import TextLink from '@divvy-web/skylab.textlink'

import { onContactUsClick } from '@sputnik/components/ContactUsButton/contactUsUtils'
import { useDocumentTypes } from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/DocumentsTabContent/useDocumentTypes'
import { GetCreditApplicationDetails_creditApplication_CreditApplication_requiredDocs_CreditApplicationDocument } from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/GetCreditApplicationDetails.gql'
import { useFileUpload } from '@sputnik/pages/DashboardPages/FileUploadStatusProvider'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'

import styles from './styles.module.scss'

export enum RejectionReason {
  BLURRY_UNREADABLE = 'BLURRY_UNREADABLE',
  EXPIRED = 'EXPIRED',
  INFO_NOT_MATCHED = 'INFO_NOT_MATCHED',
  INVALID = 'INVALID',
  OTHER = 'OTHER',
  SCREENSHOT = 'SCREENSHOT',
}

type CreditApplicationDocument =
  GetCreditApplicationDetails_creditApplication_CreditApplication_requiredDocs_CreditApplicationDocument

export interface DocumentUploadItemProps {
  salesforceCreditId: string
  document: CreditApplicationDocument
}

const DocumentUploadItem = ({
  salesforceCreditId,
  document: { documentType, status, statusMessage, fileName, documentUuid, signedUrl },
}: DocumentUploadItemProps) => {
  const [getClassName, makeTestId] = useNameFormatter('DocumentUploadItem')
  const [isShowingFile, setIsShowingFile] = useState(false)
  const { track } = useWebTracking()

  const { options } = useDocumentTypes({ salesforceCreditId })
  const { state } = useFileUpload(documentUuid)

  const { label: title, header, footer, content } = options?.find(({ value }) => value === documentType) ?? {}

  // statuses are derived from
  // https://git.hq.bill.com/backend/compliance/-/blob/master/apps/shared_db/lib/shared_db/cip_documents/cip_document_activity.ex#L13
  const { statusText, statusIcon, bannerText } = useMemo((): {
    statusText: React.ReactNode
    statusIcon: React.ReactNode
    bannerText?: React.ReactNode
  } => {
    if (state === 'loading' || status === null) {
      return {
        statusText: (
          <FormattedMessage
            defaultMessage='Uploading'
            id='sputnik.DocumentUploadItem__hsYvDX'
          />
        ),
        statusIcon: <Spinner size={SpinnerSize.XSmall} />,
      }
    }
    if (state === 'failure') {
      return {
        statusText: (
          <FormattedMessage
            defaultMessage='Upload failed. Please try again.'
            id='sputnik.DocumentUploadItem__J/1qaw'
          />
        ),
        statusIcon: (
          <Icon
            className={styles[getClassName('heading-title-denied')]}
            name='feedback-error'
          />
        ),
      }
    }
    switch (status) {
      case 'reviewed':
        return {
          statusText: (
            <FormattedMessage
              defaultMessage='Accepted'
              id='sputnik.RequiredDocument__aFyFm0'
            />
          ),
          statusIcon: (
            <Icon
              className={styles[getClassName('heading-title-approved')]}
              name='feedback-success'
            />
          ),
        }
      case 'rejected': {
        const baseMessage = {
          statusText: (
            <FormattedMessage
              defaultMessage='Rejected'
              id='sputnik.RequiredDocument__lYvasN'
            />
          ),
          statusIcon: (
            <Icon
              className={styles[getClassName('heading-title-denied')]}
              name='feedback-error'
            />
          ),
        }
        switch (statusMessage) {
          case RejectionReason.BLURRY_UNREADABLE:
            return {
              ...baseMessage,
              statusText: (
                <FormattedMessage
                  defaultMessage='Blurry or unreadable'
                  id='sputnik.DocumentUploadItem__7nKpak'
                />
              ),
              bannerText: (
                <FormattedMessage
                  defaultMessage='Unfortunately, we weren’t able to accept your document due to the image being blurry or unreadable. Please upload a clearer image so we can continue our review process.'
                  id='sputnik.DocumentUploadItem__cTxBWP'
                />
              ),
            }
          case RejectionReason.EXPIRED:
            return {
              ...baseMessage,
              statusText: (
                <FormattedMessage
                  defaultMessage='Expired document'
                  id='sputnik.DocumentUploadItem__RahCRH'
                />
              ),
              bannerText: (
                <FormattedMessage
                  defaultMessage='Unfortunately, the document presented exceeds the 90 day expiration limit. Please upload a document with a date of {date} or later.'
                  id='sputnik.DocumentUploadItem__cqvwiW'
                  values={{ date: subDays(new Date(), 90).toLocaleDateString() }}
                />
              ),
            }
          case RejectionReason.INFO_NOT_MATCHED:
            return {
              ...baseMessage,
              statusText: (
                <FormattedMessage
                  defaultMessage='Information does not match application'
                  id='sputnik.DocumentUploadItem__NpZMQr'
                />
              ),
              bannerText: (
                <FormattedMessage
                  defaultMessage='Unfortunately, the information in the document provided doesn’t match the information in your application. Please upload a document that matches the information provided in your application so we can continue our review process.'
                  id='sputnik.DocumentUploadItem__Lf1vyX'
                />
              ),
            }
          case RejectionReason.INVALID:
            return {
              ...baseMessage,
              statusText: (
                <FormattedMessage
                  defaultMessage='Invalid document'
                  id='sputnik.DocumentUploadItem__89T/ze'
                />
              ),
              bannerText: (
                <FormattedMessage
                  defaultMessage='Unfortunately, the document type provided is not valid for the verification needed. Please upload a valid document so we can continue our review process.'
                  id='sputnik.DocumentUploadItem__b5taXx'
                />
              ),
            }
          case RejectionReason.SCREENSHOT:
            return {
              ...baseMessage,
              statusText: (
                <FormattedMessage
                  defaultMessage='Screenshot'
                  id='sputnik.DocumentUploadItem__9a+SKt'
                />
              ),
              bannerText: (
                <FormattedMessage
                  defaultMessage='Unfortunately, BILL Spend & Expense is unable to accept screenshots. Please upload a file or picture of the document so we can continue our review process.'
                  id='sputnik.DocumentUploadItem__2jtEsK'
                />
              ),
            }
          case RejectionReason.OTHER:
          default:
            return {
              ...baseMessage,
              bannerText: (
                <FormattedMessage
                  defaultMessage='Unfortunately, there was an issue with the document provided. Please email us at {emailLink} or <contactUsLink>start a chat with us</contactUsLink> to resolve this matter.'
                  id='sputnik.DocumentUploadItem__y6Foi4'
                  values={{
                    emailLink: <TextLink href='mailto:help@getdivvy.com'>help@getdivvy.com</TextLink>,
                    contactUsLink: (...content) => <TextLink onClick={onContactUsClick}>{content}</TextLink>,
                  }}
                />
              ),
            }
        }
      }
      case 'uploaded':
      case 'comment':
        return {
          statusText: (
            <FormattedMessage
              defaultMessage='In review'
              id='sputnik.RequiredDocument__plemGH'
            />
          ),
          statusIcon: (
            <Icon
              className={styles[getClassName('heading-title-in-review')]}
              name='feedback-in-progress'
            />
          ),
        }
      case 'requested':
      case 'deleted':
      default:
        return {
          statusText: (
            <FormattedMessage
              defaultMessage='Not uploaded'
              id='sputnik.RequiredDocument__W93C+Q'
            />
          ),
          statusIcon: <Icon name='feedback-warning' />,
        }
    }
  }, [getClassName, state, statusMessage, status])

  return (
    <ExpansionPanel
      heading={
        <div className={styles[getClassName('heading')]}>
          {statusIcon}
          <div className={styles[getClassName('heading-title')]}>
            <div className={styles[getClassName('heading-title-document')]}>{title}</div>
            <div className={styles[getClassName('heading-title-status')]}>{statusText}</div>
          </div>
        </div>
      }
    >
      <div className={styles[getClassName('content')]}>
        {bannerText && (
          <Banner
            backgroundColor={BANNER_COLOR_DANGER}
            customIcon={<div />}
            messageContent={bannerText}
          />
        )}
        <div className={styles[getClassName('description')]}>
          <div className={styles[getClassName('description-header')]}>{header}</div>
          <div className={styles[getClassName('description-content')]}>
            {Array.isArray(content) ? (
              <ul>
                {content.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            ) : (
              content
            )}
          </div>
          <div className={styles[getClassName('description-footer')]}>{footer}</div>
        </div>
        {fileName && signedUrl && (
          <div className={styles[getClassName('file')]}>
            {statusMessage && (
              <Icon
                className={styles[getClassName('file-rejected')]}
                name='warning'
              />
            )}
            {statusMessage ? (
              <div className={styles[getClassName('file-rejected')]}>{fileName}</div>
            ) : (
              <TextLink
                onClick={() => {
                  track('se_signup__application_dashboard_documents_tab_modal_file_item__fired')
                  setIsShowingFile(true)
                }}
              >
                {fileName}
              </TextLink>
            )}
            <FileViewer
              closeOnOutsideClick
              dataTestId={makeTestId('fileviewer')}
              // passing in blank for the file type seems to work just fine
              files={[{ filename: fileName, id: documentUuid, url: signedUrl, type: '' }]}
              isShowing={isShowingFile}
              reduceWidthBy={'var(--sky-drawer-width-s)'}
              onClose={() => setIsShowingFile(false)}
            />
          </div>
        )}
      </div>
    </ExpansionPanel>
  )
}

export default DocumentUploadItem
